import { createContext, useContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useLazyGetFixedPricingLocationsQuery } from "@/redux/apis/config/trip/tripCost";
import { useAppSelector } from "@/redux/hooks";
import { fixedPricingLocationSelector, fixedPricingLocationsSelector } from "@/redux/slices/trip/selectors";
import { FixedPricingLocation } from "@/redux/slices/trip/types";
import { usePricingProfileContext } from "../PricingProfile/PricingProfileContext";

interface ZoneRatesState {
  zones: FixedPricingLocation[];
  activeZone?: FixedPricingLocation;
  zoneName: string | null;
  isFetching: boolean;
  isRestricted: boolean;
}

const ZoneRatesContext = createContext<ZoneRatesState | null>(null);

export const ZoneRatesContextProvider = ({ children }: React.PropsWithChildren) => {
  const { isFetching: isFetchingPricingProfiles, pricingProfile } = usePricingProfileContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const zoneName = searchParams.get("zoneName");
  const { slug = "" } = useParams();

  const [getZones, { isFetching: isFetchingZones, isSuccess }] =
    useLazyGetFixedPricingLocationsQuery();
  const zones = useAppSelector(fixedPricingLocationsSelector);
  const activeZone = useAppSelector(fixedPricingLocationSelector(slug));
  const [isRestricted, setIsRestricted] = useState(false);

  useEffect(() => {
    if (!isFetchingPricingProfiles && pricingProfile) getZones({ id: pricingProfile.id, page_size: "100" });
  }, [isFetchingPricingProfiles, pricingProfile, getZones]);

  // breadcrumbs setup (no extra history entries)
  useEffect(() => {
    if (!activeZone || activeZone.managed) return;

    const nextName = activeZone.pickupLocation.name;
    if (zoneName !== nextName) {
      const next = new URLSearchParams(searchParams); 
      next.set("zoneName", nextName);
      setSearchParams(next, { replace: true }); 
    }
    // Only depend on values we compare/use
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeZone?.pickupLocation.name, activeZone?.managed, zoneName, setSearchParams]);

  // restricted check
  useEffect(() => {
    if (isSuccess && (!activeZone || activeZone.managed)) setIsRestricted(true);
    else setIsRestricted(false);
  }, [isSuccess, activeZone]);

  return (
    <ZoneRatesContext.Provider
      value={{ zones, activeZone, zoneName, isFetching: isFetchingZones, isRestricted }}
    >
      {children}
    </ZoneRatesContext.Provider>
  );
};

export const useZoneRatesContext = () => {
  const context = useContext(ZoneRatesContext);
  if (!context) throw new Error("useZoneRatesContext must be used within a ZoneRatesContextProvider");

  return context;
};
